import React from 'react'

import type {PermissionCatalogEntry} from '../core/permissionGrouping'

import {GroupedPermissionCheckboxGrid} from './GroupedPermissionViews'
import type {RoleRecord} from '../core/rolePermissionRequests'
import {
  ACCESS_TEMPLATES,
  type AccessTemplateId,
  type SimpleWizardActionKey,
  type SimpleWizardModuleKey,
  SIMPLE_ACTION_LABELS,
  SIMPLE_MODULE_HELP,
  SIMPLE_WIZARD_MODULES,
} from '../core/simpleRoleWizardModel'
import SearchableSelect from '../../../shared/components/SearchableSelect'

const DATA_SCOPE_OPTIONS = ['ALL', 'COMPANY', 'VESSEL'] as const

const BEHAVIOR_TEMPLATE_OPTIONS = [
  'COMPANY_GROUP_ADMIN',
  'COMPANY_ADMIN',
  'CREW',
  'SHORE',
  'OPERATOR',
  'VENDOR',
] as const

type CreateRoleWizardBodyProps = {
  createRoleStep: 1 | 2 | 3 | 4
  crPresetCopyFromId: number | null
  rolesList: RoleRecord[]
  crDisplayName: string
  setCrDisplayName: (v: string) => void
  crDescription: string
  setCrDescription: (v: string) => void
  crActive: boolean
  setCrActive: (v: boolean) => void
  isPlatformSuperAdminUser: boolean
  crCompanyGroupAdminId: number | null
  setCrCompanyGroupAdminId: (v: number | null) => void
  crCompanyGroupOptions: {id: number; label: string}[]
  crCompanyGroupLoadError: string | null
  crShowAdvancedCreateFields: boolean
  setCrShowAdvancedCreateFields: (v: boolean) => void
  crCode: string
  setCrCode: (v: string) => void
  crScope: string
  setCrScope: (v: string) => void
  crBehaviorTemplate: string
  setCrBehaviorTemplate: (v: string) => void
  crPermMode: 'BLANK' | 'COPY' | 'MANUAL'
  setCrPermMode: (v: 'BLANK' | 'COPY' | 'MANUAL') => void
  crCopyFromId: string
  setCrCopyFromId: (v: string) => void
  crWizardPermSearch: string
  setCrWizardPermSearch: (v: string) => void
  crManualSelected: Set<string>
  toggleCrWizardPermission: (code: string) => void
  setCrManualMany: (codes: string[], selected: boolean) => void
  permissionCatalog: PermissionCatalogEntry[]
  crAccessTemplateId: AccessTemplateId
  pickAccessTemplate: (id: AccessTemplateId) => void
  crModuleFeatures: Record<SimpleWizardModuleKey, Record<SimpleWizardActionKey, boolean>>
  toggleCrModuleFeature: (mk: SimpleWizardModuleKey, ak: SimpleWizardActionKey) => void
  crWizardBundleId: number | null
  setCrWizardBundleId: (v: number | null) => void
  suggestedBundles: {id: number; code: string; name: string}[]
  crWizardExtraCodes: Set<string>
  toggleCrWizardExtraPermission: (code: string) => void
  setCrWizardExtraMany: (codes: string[], selected: boolean) => void
  crShowFineTuneExtras: boolean
  setCrShowFineTuneExtras: (v: boolean) => void
  wizardSimpleCodes: Set<string>
  wizardSubmitCodes: Set<string>
}

export function CreateRoleWizardBody(props: CreateRoleWizardBodyProps) {
  const dup = props.crPresetCopyFromId
    ? props.rolesList.find((x) => x.id === props.crPresetCopyFromId)
    : null

  return (
    <div className='modal-body'>
      {dup ? (
        <div className='alert alert-info small mb-3 py-2'>
          New role will reuse access from{' '}
          <strong>{(dup.displayName || dup.roleType || 'role').replace(/_/g, ' ')}</strong>
          {dup.code ? ` (${dup.code})` : ''}. Company scope and name below still apply.
        </div>
      ) : null}

      {props.createRoleStep === 1 && (
        <div className='row g-3'>
          <div className='col-12'>
            <div className='fw-bold text-gray-900 fs-6'>Step 1 — Role basics</div>
            <p className='small text-muted mb-0'>Name how this role appears to administrators and crew.</p>
          </div>
          <div className='col-md-8'>
            <label className='form-label fw-bold'>Role name</label>
            <input
              className='form-control'
              value={props.crDisplayName}
              onChange={(e) => props.setCrDisplayName(e.target.value)}
              placeholder='e.g. Procurement coordinator'
            />
          </div>
          <div className='col-md-4 d-flex align-items-end'>
            <div className='form-check mb-2'>
              <input
                id='cr-active'
                type='checkbox'
                className='form-check-input'
                checked={props.crActive}
                onChange={(e) => props.setCrActive(e.target.checked)}
              />
              <label className='form-check-label' htmlFor='cr-active'>
                Active
              </label>
            </div>
          </div>
          <div className='col-12'>
            <label className='form-label fw-bold'>Description</label>
            <textarea
              className='form-control'
              rows={2}
              value={props.crDescription}
              onChange={(e) => props.setCrDescription(e.target.value)}
              placeholder='What is this role for?'
            />
          </div>
          {props.isPlatformSuperAdminUser ? (
            <div className='col-12 col-md-8'>
              <label className='form-label fw-bold'>Company scope</label>
              <SearchableSelect
                className='form-select'
                value={props.crCompanyGroupAdminId == null ? '' : String(props.crCompanyGroupAdminId)}
                onChange={(e) => {
                  const v = e.target.value
                  props.setCrCompanyGroupAdminId(v === '' ? null : Number(v))
                }}
              >
                <option value=''>Select company group...</option>
                {props.crCompanyGroupOptions.map((o) => (
                  <option key={o.id} value={String(o.id)}>
                    {o.label} (company group #{o.id})
                  </option>
                ))}
              </SearchableSelect>
              <p className='small text-muted mb-0 mt-1'>
                Custom roles must be scoped to a company group.
              </p>
              {props.crCompanyGroupLoadError ? (
                <p className='small text-danger mb-0 mt-2'>{props.crCompanyGroupLoadError}</p>
              ) : null}
            </div>
          ) : (
            <div className='col-12'>
              <label className='form-label fw-bold'>Company scope</label>
              <div className='form-control-plaintext border rounded px-3 py-2 bg-light small text-gray-800'>
                Company-specific role for your organization
              </div>
            </div>
          )}

          <div className='col-12'>
            <button
              type='button'
              className='btn btn-link btn-sm text-decoration-none p-0 fw-semibold'
              onClick={() => props.setCrShowAdvancedCreateFields(!props.crShowAdvancedCreateFields)}
            >
              {props.crShowAdvancedCreateFields ? '▼' : '▶'} Advanced settings (optional)
            </button>
            {props.crShowAdvancedCreateFields ? (
              <div className='border rounded p-3 mt-2 bg-light'>
                <div className='row g-2'>
                  <div className='col-md-6'>
                    <label className='form-label small fw-bold mb-1'>Role code (internal)</label>
                    <input
                      className='form-control form-control-sm font-monospace'
                      value={props.crCode}
                      onChange={(e) => props.setCrCode(e.target.value)}
                      placeholder='Leave blank to let the server assign'
                    />
                  </div>
                  <div className='col-md-6'>
                    <label className='form-label small fw-bold mb-1'>Default data scope</label>
                    <SearchableSelect
                      className='form-select form-select-sm'
                      value={props.crScope}
                      onChange={(e) => props.setCrScope(e.target.value)}
                    >
                      {DATA_SCOPE_OPTIONS.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </SearchableSelect>
                  </div>
                  <div className='col-12'>
                    <label className='form-label small fw-bold mb-1'>Behavior template</label>
                    <SearchableSelect
                      className='form-select form-select-sm'
                      value={props.crBehaviorTemplate}
                      onChange={(e) => props.setCrBehaviorTemplate(e.target.value)}
                    >
                      {BEHAVIOR_TEMPLATE_OPTIONS.map((t) => (
                        <option key={t} value={t}>
                          {t.replace(/_/g, ' ')}
                        </option>
                      ))}
                    </SearchableSelect>
                  </div>
                  <div className='col-12'>
                    <div className='fw-bold small mb-1'>Permission source (overrides guided steps)</div>
                    <div className='form-check'>
                      <input
                        id='cr-mode-blank'
                        className='form-check-input'
                        type='radio'
                        name='crPermMode'
                        checked={props.crPermMode === 'BLANK'}
                        onChange={() => props.setCrPermMode('BLANK')}
                      />
                      <label className='form-check-label small' htmlFor='cr-mode-blank'>
                        Start with no default permissions
                      </label>
                    </div>
                    <div className='form-check'>
                      <input
                        id='cr-mode-copy'
                        className='form-check-input'
                        type='radio'
                        name='crPermMode'
                        checked={props.crPermMode === 'COPY'}
                        onChange={() => props.setCrPermMode('COPY')}
                      />
                      <label className='form-check-label small' htmlFor='cr-mode-copy'>
                        Copy from an existing role
                      </label>
                    </div>
                    <div className='form-check'>
                      <input
                        id='cr-mode-manual'
                        className='form-check-input'
                        type='radio'
                        name='crPermMode'
                        checked={props.crPermMode === 'MANUAL'}
                        onChange={() => props.setCrPermMode('MANUAL')}
                      />
                      <label className='form-check-label small' htmlFor='cr-mode-manual'>
                        Pick from full checklist
                      </label>
                    </div>
                  </div>
                  {props.crPermMode === 'COPY' && (
                    <div className='col-12'>
                      <label className='form-label small fw-bold'>Copy from</label>
                      <SearchableSelect
                        className='form-select form-select-sm'
                        value={props.crCopyFromId}
                        onChange={(e) => props.setCrCopyFromId(e.target.value)}
                      >
                        <option value=''>— Select role —</option>
                        {props.rolesList.map((r) => (
                          <option key={r.id} value={r.id}>
                            {(r.displayName || r.roleType || `Role ${r.id}`).replace(/_/g, ' ')} (#{r.id})
                          </option>
                        ))}
                      </SearchableSelect>
                    </div>
                  )}
                  {props.crPermMode === 'MANUAL' && (
                    <div className='col-12'>
                      <label className='form-label small fw-bold'>Search</label>
                      <input
                        className='form-control form-control-sm mb-2'
                        value={props.crWizardPermSearch}
                        onChange={(e) => props.setCrWizardPermSearch(e.target.value)}
                      />
                      <div className='border rounded bg-white' style={{maxHeight: 'min(320px, 40vh)', overflow: 'auto'}}>
                        <GroupedPermissionCheckboxGrid
                          catalog={props.permissionCatalog}
                          searchQuery={props.crWizardPermSearch}
                          selectedCodes={props.crManualSelected}
                          disabled={false}
                          onToggle={props.toggleCrWizardPermission}
                          bulk={{
                            totalCatalogCount: props.permissionCatalog.length,
                            onSetMany: props.setCrManualMany,
                            globalLegend: 'selected',
                          }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : null}
          </div>
        </div>
      )}

      {props.createRoleStep === 2 && (
        <div>
          <div className='fw-bold text-gray-900 fs-6 mb-1'>Step 2 — Access template</div>
          <p className='small text-muted mb-3'>
            Pick a starting point. This sets sensible defaults; you will fine-tune access next.
          </p>
          <div className='row g-3'>
            {ACCESS_TEMPLATES.map((t) => {
              const selected = props.crAccessTemplateId === t.id
              return (
                <div className='col-md-6' key={t.id}>
                  <button
                    type='button'
                    className={`btn w-100 text-start h-100 p-3 border rounded-3 ${
                      selected ? 'border-primary border-2 bg-primary bg-opacity-10' : 'border bg-white'
                    }`}
                    onClick={() => props.pickAccessTemplate(t.id)}
                  >
                    <div className='fw-bold text-gray-900'>{t.title}</div>
                    <div className='small text-muted mt-1' style={{lineHeight: 1.4}}>
                      {t.description}
                    </div>
                  </button>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {props.createRoleStep === 3 && !dup && !props.crShowAdvancedCreateFields && (
        <div className='d-flex flex-column gap-3'>
          <div>
            <div className='fw-bold text-gray-900 fs-6'>Step 3 — Feature access</div>
            <p className='small text-muted mb-0'>
              Adjust by area. We map your choices to the same permissions the platform already uses — nothing is
              simulated only in the browser.
            </p>
          </div>

          {props.suggestedBundles.length > 0 ? (
            <div>
              <label className='form-label fw-bold mb-1'>Start from access package (optional)</label>
              <SearchableSelect
                className='form-select'
                value={props.crWizardBundleId ?? ''}
                onChange={(e) => props.setCrWizardBundleId(e.target.value ? Number(e.target.value) : null)}
              >
                <option value=''>No package — use checklist only</option>
                {props.suggestedBundles.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name} ({b.code})
                  </option>
                ))}
              </SearchableSelect>
              <p className='small text-muted mb-0 mt-1'>
                Packages are the same tiles you assign to users under <strong>Assign packages</strong>. Selecting one adds
                its permissions on top of the toggles below.
              </p>
            </div>
          ) : null}

          <div className='table-responsive border rounded'>
            <table className='table table-sm align-middle mb-0'>
              <thead className='table-light'>
                <tr className='small text-uppercase text-muted'>
                  <th>Area</th>
                  {(
                    Object.keys(SIMPLE_ACTION_LABELS) as SimpleWizardActionKey[]
                  ).map((k) => (
                    <th key={k} className='text-center text-nowrap'>
                      {SIMPLE_ACTION_LABELS[k]}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {SIMPLE_WIZARD_MODULES.map((row) => {
                  const mk = row.key
                  const help = SIMPLE_MODULE_HELP[mk]
                  return (
                    <tr key={mk}>
                      <td className='small'>
                        <span className='fw-semibold text-gray-900'>{row.label}</span>
                        {help ? (
                          <div className='text-muted' style={{fontSize: '0.72rem', lineHeight: 1.35}}>
                            {help}
                          </div>
                        ) : null}
                      </td>
                      {(Object.keys(SIMPLE_ACTION_LABELS) as SimpleWizardActionKey[]).map((ak) => (
                        <td key={ak} className='text-center'>
                          <input
                            type='checkbox'
                            className='form-check-input'
                            checked={props.crModuleFeatures[mk][ak]}
                            onChange={() => props.toggleCrModuleFeature(mk, ak)}
                            title={`${row.label} — ${SIMPLE_ACTION_LABELS[ak]}`}
                          />
                        </td>
                      ))}
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          <div className='alert alert-light border small mb-0 py-2'>
            <strong>Fine tune permissions.</strong> Most roles do not need this. The full checklist mirrors{' '}
            <strong>Role permissions</strong> under Advanced tools after the role exists.
          </div>
          <button
            type='button'
            className='btn btn-sm btn-light border'
            onClick={() => props.setCrShowFineTuneExtras(!props.crShowFineTuneExtras)}
          >
            {props.crShowFineTuneExtras ? 'Hide' : 'Show'} extra permissions checklist
          </button>
          {props.crShowFineTuneExtras ? (
            <div>
              <label className='form-label fw-bold small'>Add extra permissions (optional)</label>
              <input
                className='form-control form-control-sm mb-2'
                placeholder='Search…'
                value={props.crWizardPermSearch}
                onChange={(e) => props.setCrWizardPermSearch(e.target.value)}
              />
              <div className='border rounded bg-white' style={{maxHeight: 'min(280px, 38vh)', overflow: 'auto'}}>
                <GroupedPermissionCheckboxGrid
                  catalog={props.permissionCatalog}
                  searchQuery={props.crWizardPermSearch}
                  selectedCodes={props.crWizardExtraCodes}
                  disabled={false}
                  onToggle={props.toggleCrWizardExtraPermission}
                  bulk={{
                    totalCatalogCount: props.permissionCatalog.length,
                    onSetMany: props.setCrWizardExtraMany,
                    globalLegend: 'extra',
                  }}
                />
              </div>
              <p className='small text-muted mb-0 mt-1'>
                Only additional codes are tracked here; checklist toggles already cover broad access.
              </p>
            </div>
          ) : null}
        </div>
      )}

      {props.createRoleStep === 3 && (dup || props.crShowAdvancedCreateFields) && (
        <div className='small text-muted'>
          When duplicating a role or using <strong>Advanced tools</strong>, skip the simple checklist — permissions
          come from your advanced choice. Continue to review.
        </div>
      )}

      {props.createRoleStep === 4 && (
        <div className='d-flex flex-column gap-2'>
          <div className='fw-bold text-gray-900 fs-6'>Step 4 — Review</div>
          <ul className='small mb-0 ps-3'>
            <li>
              <strong>Role name:</strong> {props.crDisplayName.trim() || '—'}
            </li>
            <li>
              <strong>Company scope:</strong>{' '}
              {props.isPlatformSuperAdminUser
                ? props.crCompanyGroupAdminId == null
                  ? 'Select a company group'
                  : `Company group #${props.crCompanyGroupAdminId}`
                : 'Your organization'}
            </li>
            <li>
              <strong>Access template:</strong>{' '}
              {ACCESS_TEMPLATES.find((t) => t.id === props.crAccessTemplateId)?.title || props.crAccessTemplateId}
            </li>
            <li>
              <strong>Permissions to save:</strong>{' '}
              {dup || (props.crShowAdvancedCreateFields && props.crPermMode === 'COPY' && props.crCopyFromId)
                ? 'Copied from selected role'
                : props.crShowAdvancedCreateFields && props.crPermMode === 'MANUAL'
                  ? `${props.crManualSelected.size} (advanced checklist)`
                  : `${props.wizardSubmitCodes.size} (feature access${
                      props.crWizardBundleId ? ' + package' : ''
                    })`}
            </li>
          </ul>
          {!dup &&
            !(props.crShowAdvancedCreateFields && props.crPermMode !== 'BLANK') &&
            props.wizardSubmitCodes.size === 0 &&
            props.crAccessTemplateId !== 'blank' && (
              <div className='alert alert-warning small mb-0 py-2'>
                This role has <strong>no permissions</strong> yet. You can still create it and configure access later in{' '}
                <strong>Role permissions</strong>.
              </div>
            )}
        </div>
      )}
    </div>
  )
}
